import { useCallback, useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import { useAxios, APIError } from '../../core/_axios';
import { Account, User } from '../../core/_models';
import { managementGetUserByUniqueIdentifier } from '../../core/_requests';
import usePageTitle from '../../hooks/usePageTitle';
import ManageAccountForm from './ManageAccountForm';
import { UsersList } from '../../components/UsersList';
import ManageAccountUserForm from './ManageAccountUserForm';
import ManageUserForm from './ManageUserForm';
import { CONTAINER_CONTENT_CLASS } from '../signatures/Signatures';

export function ManageSpecificAccountUserRoutes() {
    const axios = useAxios();

    const {
        pathname
    } = useLocation();

    const params = useParams();

    usePageTitle('Manage User');

    const [loading, setLoading] = useState(true);
    const [loadingError, setLoadingError] = useState<string | null>(null);

    const [user, setUser] = useState<User | null>(null);

    const loadUser = useCallback(async () => {
        if (!params.userUniqueId) {
            setLoading(false);
            setLoadingError('No user unique identifier provided.');
            return;
        }
        setLoading(true);
        setLoadingError(null);
        try {
            const {
                user: updatedUser
            } = await managementGetUserByUniqueIdentifier(axios, params.userUniqueId);
            setUser(updatedUser);
        } catch (exception) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'An unknown error has occurred. Please try again.'
            setLoadingError(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [axios, params.userUniqueId]);

    useEffect(() => {
        loadUser();
    }, [loadUser]);

    if (loading) {
        return (
            <div className="container-fixed">
                <LoadingSpinner />
            </div>
        );
    }

    if (loadingError) {
        return (
            <div className="container-fixed">
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative" role="alert">
                    <span className="block sm:inline">
                        {loadingError}
                    </span>
                </div>
                <div className="mt-5">
                    <button className="btn btn-lg btn-light" onClick={loadUser}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return <>
        <div className="container-fixed">
            <div className={CONTAINER_CONTENT_CLASS}>
                <div className="flex flex-col justify-center gap-2">
                    <h1 className="text-xl font-semibold leading-none text-gray-900">
                        {`Manage ${user.public_data.name || 'User'}`}
                    </h1>
                    <div className="flex items-center gap-2 text-sm font-medium text-gray-600">
                        {/* subtitle? */}
                    </div>
                </div>
                {false && <div className="flex items-center gap-2.5">
                    <a className="btn btn-sm btn-light" href="#">
                        Go to Teams
                    </a>
                </div>}
            </div>
        </div>
        <div className="container-fixed">
            <div className='mb-5'>
                <ul className="flex flex-wrap text-sm font-medium text-center text-gray-500 border-b border-gray-200 dark:border-gray-700 dark:text-gray-400">
                    <li className="me-2">
                        <Link to={`/management/users/${user.unique_identifier}`}
                            className={[
                                pathname === `/management/users/${user.unique_identifier}/details` ?
                                    'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                    'text-gray-500 dark:text-gray-400',
                                'inline-block p-4'
                            ].join(' ')}>
                            Details
                        </Link>
                    </li>
                    {false && <>
                        <li className="me-2">
                            <Link to={`/management/accounts/${user.unique_identifier}/users`}
                                className={[
                                    pathname === `/management/accounts/${user.unique_identifier}/users` ?
                                        'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                        'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}>
                                Users
                            </Link>
                        </li>
                        <li className="me-2">
                            <Link to={`/management/accounts/${user.unique_identifier}/add-user`}
                                className={[
                                    pathname === `/management/accounts/${user.unique_identifier}/add-user` ?
                                        'text-blue-600 bg-gray-100 rounded-t-lg active dark:bg-gray-800 dark:text-blue-500' :
                                        'text-gray-500 dark:text-gray-400',
                                    'inline-block p-4'
                                ].join(' ')}>
                                Add User
                            </Link>
                        </li>
                    </>}
                </ul>
            </div>
            <Routes>
                <Route path="/details" element={
                    <ManageUserForm
                        user={user}
                        setUser={setUser}
                    />}
                />
                <Route
                    path="*"
                    element={<Navigate
                        to={`/management/users/${user.unique_identifier}/details`}
                        replace
                    />}
                />
            </Routes>
        </div>
    </>
}